import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MuiPickersUtilsProvider,
  Paper,
  TextField,
  Typography,
} from '@dls/react-core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker } from '@dls/react-mui-datepicker';
import { Icon } from '@dls/react-icon';
import { Grid } from '@material-ui/core';
import { GridFormElement } from '../form-controls';
import { getMeasurementTypes, MeasurementType } from '../../api/studykit';
import { endOfDay, startOfDay } from 'date-fns';
import { MetricExporter } from '../subjects/metric-exporter';
import { useSubjectContext } from '../../pages/subject';
import { StudyPageContext } from '../../App';

interface ExportDataProps {
  open: boolean;
  onClose: () => void;
}

export const ExportData: React.FC<ExportDataProps> = ({ open, onClose }) => {
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [measurementTypes, setMeasurementTypes] = useState<MeasurementType[]>([]);
  const [selectedMeasurements, setSelectedMeasurements] = useState<MeasurementType[]>([]);
  const { study } = useContext(StudyPageContext);
  const { subject } = useSubjectContext();

  useEffect(() => {
    getMeasurementTypes(study.id, subject.id).then(setMeasurementTypes);
  }, [study.id, subject.id]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date || new Date());
  };

  const resetAndClose = () => {
    setSelectedMeasurements([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      PaperProps={{ style: { width: `50vw`, maxWidth: `600px` } }}
    >
      <DialogTitle disableTypography>
        <Typography variant="body2" weight={'bold'}>
          Export Metric Data
        </Typography>
      </DialogTitle>
      <Divider orientation={'horizontal'} />
      <DialogContent>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Grid container spacing={3}>
            <GridFormElement xs={12} md={12} label={'Select Date'}>
              <KeyboardDatePicker
                disableToolbar
                autoOk
                variant="inline"
                format="dd-MM-yyyy"
                id="date-picker-inline"
                value={selectedDate}
                clearable={false}
                onChange={handleDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                PopoverProps={{ PaperProps: { className: `DlsDatePicker`, component: Paper } }}
                keyboardIcon={<Icon name={'Calendar'} size={'m'} />}
                leftArrowIcon={<Icon name={'NavigationLeft'} size={'m'} />}
                rightArrowIcon={<Icon name={'NavigationRight'} size={'m'} />}
              />
            </GridFormElement>
            <GridFormElement xs={12} md={12} label={'Select Metrics'}>
              <Autocomplete
                multiple
                PaperComponent={Paper}
                classes={{
                  paper: 'DLS-Autocomplete',
                }}
                renderTags={(value: MeasurementType[], getTagProps) =>
                  value.map((option: MeasurementType, index: number) => (
                    <Chip variant="outlined" label={option.label} {...getTagProps({ index })} />
                  ))
                }
                getOptionLabel={(m) => m.label}
                options={measurementTypes}
                onChange={(_, v: MeasurementType[]) => setSelectedMeasurements([...v])}
                disableClearable
                renderInput={(params: any) => <TextField {...params} variant="standard" />}
              />
            </GridFormElement>
            <Divider orientation={'horizontal'} />
          </Grid>
        </MuiPickersUtilsProvider>
      </DialogContent>
      <Divider orientation={'horizontal'} />
      <DialogActions style={{ justifyContent: `space-between` }}>
        <MetricExporter>
          {({ isSubmitting, triggerExport }) => (
            <>
              <Button
                style={{ width: `8rem` }}
                disabled={isSubmitting}
                onClick={resetAndClose}
                dls_variant={'quietDefault'}
              >
                Cancel
              </Button>
              <Button
                disabled={isSubmitting || !selectedMeasurements.length}
                onClick={() => {
                  triggerExport(
                    startOfDay(selectedDate),
                    endOfDay(selectedDate),
                    selectedMeasurements.map((m) => m.sourceFormatId),
                  ).then(resetAndClose);
                }}
                dls_variant={'primary'}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress variant={'indeterminate'} dls_size={'small'} />
                  ) : (
                    <Icon name={'DocumentDownload32'} size={'m'} />
                  )
                }
              >
                Export
              </Button>
            </>
          )}
        </MetricExporter>
      </DialogActions>
    </Dialog>
  );
};
